import React, { FC, useState } from 'react';

import { Button, DateRangePicker, type DateRangeValue, Popover, PopoverContent, PopoverTrigger } from '@column/ui';

import { getDateFormat } from '~/util';

import { TableFilterTrigger } from './TableFilterTrigger';

export interface TableDateFilterValue {
  fromDate?: string;
  toDate?: string;
}

interface TableDateFilterProps {
  value: TableDateFilterValue;
  onChange: (value: TableDateFilterValue) => void;
  label?: string;
  disabledDays?: React.ComponentProps<typeof DateRangePicker>['disabledDays'];
}

const parseDate = (value?: string) => {
  const [year, month, day] = value?.split('-').map(Number) ?? [];
  return year && month && day ? new Date(year, month - 1, day) : undefined;
};

const formatShortDate = (date: Date, withYear: boolean) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear && { year: 'numeric' }) });

const formatRangeLabel = ({ from, to }: DateRangeValue) => {
  if (!from || !to) return undefined;
  if (from.getTime() === to.getTime()) return formatShortDate(to, true);
  return `${formatShortDate(from, from.getFullYear() !== to.getFullYear())} – ${formatShortDate(to, true)}`;
};

const toRange = ({ fromDate, toDate }: TableDateFilterValue): DateRangeValue => ({
  from: parseDate(fromDate),
  to: parseDate(toDate),
});

export const TableDateFilter: FC<TableDateFilterProps> = ({ value, onChange, label = 'Date', disabledDays }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRangeValue>(() => toRange(value));
  const isActive = Boolean(value.fromDate || value.toDate);

  const submit = (range: DateRangeValue) => {
    onChange({
      fromDate: range.from ? getDateFormat(range.from) : undefined,
      toDate: range.to ? getDateFormat(range.to) : undefined,
    });
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setDraft(toRange(value));
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <TableFilterTrigger isActive={isActive} isOpen={open}>
          {formatRangeLabel(toRange(value)) ?? label}
        </TableFilterTrigger>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="flex w-auto flex-col gap-3 p-3">
        <DateRangePicker value={draft} onValueChange={setDraft} disabledDays={disabledDays} inputSize="sm" />
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => submit({ from: undefined, to: undefined })}
          >
            Clear
          </Button>
          <Button type="button" size="sm" disabled={!draft.from || !draft.to} onClick={() => submit(draft)}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
